import {
  isUserActivityResponsibleOrAbove,
  isUserCCResponsibleOrAbove,
  isUserActionResponsibleOrAbove,
  validateAddApproverRequest,
  validateUpdateActivityApprovalRequest,
  validateRemoveApproverRequest,
  isUserApprover,
  validateAddWatcherRequest,
  validateRemoveWatcherRequest,
  validateAddCompletionCriterionRequest,
  validateUpdateCCRequest,
  validateRemoveCCRequest,
  validateAddActivityRequest,
  validateUpdateActivityRequest,
  validateAddPredecessorToActivityRequest,
  validateRemovePredecessorFromActivityRequest,
  validateAddSucccessorToActivityRequest,
  validateRemoveSuccessorFromActivityRequest,
  validateAddActionRequest,
  validateUpdateActionRequest,
  validateRemoveActionRequest,
  validateAddDocumentToActivityRequest,
  validateUpdateActivityDocumentRequest,
  validateRemoveDocumentFromActivityRequest,
  validateActivityAddLinkRequest,
  validateRemoveLinkFromActivityRequest,
  isActivityChildInProgressOrDone,
  isActivityChildInProgress,
  validateAddTagToActivityRequest,
  validateRemoveTagFromActivityRequest,
  STATES,
  validateMoveCompletionCriterionRequest,
  validateMoveActionRequest,
  validateCompletionCriterionConvertToActivityRequest,
  validateActionConvertToActivityRequest,
  validateRequestActivityApprovalRequest,
} from '@beeboard/common';
import {
  Project,
  ActivityUpdate,
  ActivityDetails,
  ActivityCreateRequest,
  CompletionCriterionRequest,
  ActionUpdateRequest,
  CompletionCriterionUpdateRequest,
  ActivityDocument,
  UpdateActivityDocumentRequest,
  ActionRequest,
  Approval,
  ActivityTagCreate,
} from '@beeboard/common';
import { defineStore } from 'pinia';
import { ItemMode, TaskItemState } from '@/common';
import { useActivityProjectStore } from './activityProjectStore';
import { useIoStore } from './ioStore';
import { useProfileStore } from './profileStore';
import { useProjectRulesStore } from './projectRulesStore';
import { useProjectStore } from './projectStore';

export const useActivityProjectRulesStore = defineStore(
  'activityProjectRulesStore',
  {
    getters: {
      user() {
        return useProfileStore().profile;
      },

      project() {
        return (projectId: string): Project => {
          const project = useProjectStore().project(projectId);

          return project;
        };
      },

      projectMembers() {
        return (projectId: string) => {
          const project = useProjectStore().projectMembers(projectId);

          return project;
        };
      },

      activity() {
        return (projectId: string, activityId: string) => {
          const activity = useActivityProjectStore().activity(
            projectId,
            activityId,
          );

          return activity;
        };
      },

      activityFromProject() {
        return (projectId: string, activityId: string) => {
          const activity = useProjectStore().activitySummary(
            projectId,
            activityId,
          );

          return activity as ActivityDetails;
        };
      },

      // A summary lists its predecessors only; its successors are the
      // activities that list it as theirs. A move checks both (WI #5795).
      activityFromProjectWithSuccessors() {
        return (projectId: string, activityId: string) => {
          const successors = this.project(projectId)
            .activities.filter(({ predecessors }) =>
              predecessors?.includes(activityId),
            )
            .map(({ id }) => id);

          return {
            ...this.activityFromProject(projectId, activityId),
            successors,
          } as ActivityDetails;
        };
      },

      activityFromIoStore() {
        return (activityId: string) => {
          const activity = useIoStore().activity(activityId);

          return activity;
        };
      },

      /**
       * A function that retrieves an activity from any source
       * (either from the Activity store or from the Project store).
       *
       * This is useful when reusing validation logic on Map and on Details pages.
       */
      activityFromAnywhere() {
        return (projectId: string, activityId: string) => {
          const activity = this.hasActivity(projectId, activityId)
            ? this.activity(projectId, activityId)
            : this.activityFromProject(projectId, activityId);

          return activity;
        };
      },

      hasActivity() {
        return (projectId: string, activityId: string) => {
          return useActivityProjectStore().hasActivity(projectId, activityId);
        };
      },

      action() {
        return (projectId: string, activityId: string, actionId: string) => {
          return useActivityProjectStore().action(
            projectId,
            activityId,
            actionId,
          );
        };
      },

      actionFromProject() {
        return (projectId: string, activityId: string, actionId: string) => {
          const activity = useProjectStore().activitySummary(
            projectId,
            activityId,
          );

          const action = activity.actions.find((a) => a.id === actionId);

          if (!action) {
            throw new Error(
              `Missing action ${actionId} in activity ${activityId}`,
            );
          }

          return action;
        };
      },

      /**
       * A function that retrieves a action from any source
       * (either from the Activity store or from the Project store).
       *
       * This is useful when reusing validation logic on Map and on Details pages.
       */
      actionFromAnywhere() {
        return (projectId: string, activityId: string, actionId: string) => {
          const action =
            this.activityFromAnywhere(projectId, activityId).actions.find(
              (a) => a.id === actionId,
            ) ??
            this.activityFromProject(projectId, activityId).actions.find(
              (a) => a.id === actionId,
            );

          if (!action) {
            throw new Error(
              `Missing action ${actionId} in activity ${activityId}`,
            );
          }

          return action;
        };
      },

      criterion() {
        return (projectId: string, activityId: string, criterionId: string) => {
          return useActivityProjectStore().criterion(
            projectId,
            activityId,
            criterionId,
          );
        };
      },

      criterionFromProject() {
        return (projectId: string, activityId: string, criterionId: string) => {
          const activity = useProjectStore().activitySummary(
            projectId,
            activityId,
          );
          const criterion = activity.completion_criteria.find(
            (cc) => cc.id === criterionId,
          );

          if (!criterion) {
            throw new Error(
              `Missing criterion ${criterionId} in activity ${activityId}`,
            );
          }

          return criterion;
        };
      },

      /**
       * A function that retrieves a criterion from any source
       * (either from the Activity store or from the Project store).
       *
       * This is useful when reusing validation logic on Map and on Details pages.
       */
      criterionFromAnywhere() {
        return (projectId: string, activityId: string, criterionId: string) => {
          const criterion =
            this.activityFromAnywhere(
              projectId,
              activityId,
            ).completion_criteria.find((cc) => cc.id === criterionId) ??
            this.activityFromProject(
              projectId,
              activityId,
            ).completion_criteria.find((cc) => cc.id === criterionId);
          if (!criterion) {
            throw new Error(
              `Missing criterion ${criterionId} in activity ${activityId}`,
            );
          }

          return criterion;
        };
      },
    },

    actions: {
      isActivityResponsibleOrAbove(projectId: string, activityId: string) {
        const project = this.project(projectId);

        return isUserActivityResponsibleOrAbove(
          this.user.id,
          project,
          this.activityFromProject(projectId, activityId),
          project.editor_ids,
        );
      },

      isCCResponsibleOrAbove(
        projectId: string,
        activityId: string,
        criterionId: string,
      ) {
        const project = this.project(projectId);
        const activity = this.activityFromAnywhere(projectId, activityId);
        const criterion = this.criterionFromAnywhere(
          projectId,
          activityId,
          criterionId,
        );

        return isUserCCResponsibleOrAbove(
          this.user.id,
          project,
          activity,
          criterion,
          project.editor_ids,
        );
      },

      isActionResponsibleOrAbove(
        projectId: string,
        activityId: string,
        actionId: string,
      ) {
        const project = this.project(projectId);
        const activity = this.activityFromAnywhere(projectId, activityId);
        const action = this.actionFromAnywhere(projectId, activityId, actionId);

        return isUserActionResponsibleOrAbove(
          this.user.id,
          project,
          activity,
          action,
          project.editor_ids,
        );
      },

      validateAddActivityRequest(
        projectId: string,
        activityCreateRequest: ActivityCreateRequest,
      ) {
        return validateAddActivityRequest(
          this.project(projectId),
          activityCreateRequest,
        );
      },

      /** `activityIds`: the set a multi-move moves together (WI #5795). */
      validateUpdateActivityRequest(
        projectId: string,
        activityId: string,
        activityPayload: ActivityUpdate,
        activityIds?: string[],
      ) {
        const activity = this.hasActivity(projectId, activityId)
          ? this.activity(projectId, activityId)
          : this.activityFromProjectWithSuccessors(projectId, activityId);

        return validateUpdateActivityRequest(
          this.project(projectId),
          activity,
          activityPayload,
          activityIds,
        );
      },

      isActivityDone(projectId: string, activityId: string) {
        return this.activityFromProject(projectId, activityId).state === 'done';
      },

      isActivityActionsDisabled(projectId: string, activityId: string) {
        return (
          !this.isActivityResponsibleOrAbove(projectId, activityId) ||
          this.isActivityDone(projectId, activityId)
        );
      },

      isActivityStatusChangeDisabled(projectId: string, activityId: string) {
        return (
          !this.isActivityResponsibleOrAbove(projectId, activityId) ||
          this.isParentDone(projectId, activityId)
        );
      },

      isTaskMoveDisabled(
        projectId: string,
        activityId: string,
        taskId: string,
        isAction: boolean,
      ) {
        return (
          !this.isActivityResponsibleOrAbove(projectId, activityId) ||
          this.isActivityDone(projectId, activityId) ||
          (!isAction
            ? this.isCCDone(projectId, activityId, taskId)
            : this.isActionDone(projectId, activityId, taskId))
        );
      },

      isTaskToActivityDisabled(
        projectId: string,
        taskState: STATES,
        deadline: Date,
        projectStartDate: Date,
        targetPhaseState: STATES,
        isAction: boolean,
      ) {
        const editorLock =
          !useProjectRulesStore().isProjectEditorOrAbove(projectId);

        const validationFailed = isAction
          ? validateActionConvertToActivityRequest(
              projectStartDate,
              targetPhaseState,
              taskState,
              deadline,
            )
          : validateCompletionCriterionConvertToActivityRequest(
              projectStartDate,
              targetPhaseState,
              taskState,
              deadline,
            );

        return editorLock || validationFailed;
      },

      isChildInProgressOrDone(projectId: string, activityId: string) {
        return isActivityChildInProgressOrDone(
          this.activityFromProject(projectId, activityId),
        );
      },

      isChildInProgress(
        projectId: string,
        activityId: string,
        approvals: Approval[],
      ) {
        return isActivityChildInProgress(
          this.activityFromProject(projectId, activityId),
          approvals,
        );
      },

      // logic from https://dev.azure.com/project-deck/DECK/_workitems/edit/447
      isStateDisabled(
        fullState: ItemMode | string,
        activityId: string,
        projectId: string,
      ): boolean {
        const [toState] = fullState.split('-');
        const activity = this.activityFromAnywhere(projectId, activityId);

        return (
          activity.state === 'unplanned' ||
          toState === 'unplanned' ||
          (toState === 'planned' &&
            this.isChildInProgressOrDone(projectId, activity.id))
        );
      },

      isParentDone(projectId: string, activityId: string) {
        return (
          useProjectStore().phase(
            projectId,
            this.activityFromProject(projectId, activityId).phase,
          ).state === 'done'
        );
      },

      validateAddDocumentToActivityRequest(
        projectId: string,
        activityId: string,
        addDocumentRequest: ActivityDocument,
      ) {
        return validateAddDocumentToActivityRequest(
          this.project(projectId),
          this.activity(projectId, activityId),
          addDocumentRequest,
        );
      },

      validateUpdateActivityDocumentRequest(
        projectId: string,
        activityId: string,
        updateDocumentRequest: UpdateActivityDocumentRequest,
        documentId: string,
      ) {
        return validateUpdateActivityDocumentRequest(
          this.project(projectId),
          this.activity(projectId, activityId),
          updateDocumentRequest,
          documentId,
        );
      },

      validateRemoveDocumentFromActivityRequest(
        projectId: string,
        activityId: string,
        documentId: string,
      ) {
        return validateRemoveDocumentFromActivityRequest(
          this.project(projectId),
          this.activity(projectId, activityId),
          documentId,
        );
      },

      validateAddPredecessorToActivityRequest(
        projectId: string,
        predecessorId: string,
        successorId: string,
      ) {
        return validateAddPredecessorToActivityRequest(
          this.project(projectId),
          predecessorId,
          this.activityFromProject(projectId, successorId),
        );
      },

      validateRemovePredecessorFromActivityRequest(
        projectId: string,
        successorId: string,
        predecessorId: string,
      ) {
        return validateRemovePredecessorFromActivityRequest(
          this.project(projectId),
          this.activityFromProject(projectId, successorId),
          predecessorId,
        );
      },

      validateAddSucccessorToActivityRequest(
        projectId: string,
        predecessorId: string,
        successorId: string,
      ) {
        return validateAddSucccessorToActivityRequest(
          this.project(projectId),
          this.activity(projectId, predecessorId),
          successorId,
        );
      },

      validateRemoveSuccessorFromActivityRequest(
        projectId: string,
        predecessorId: string,
        successorId: string,
      ) {
        return validateRemoveSuccessorFromActivityRequest(
          this.project(projectId),
          this.activity(projectId, predecessorId),
          successorId,
        );
      },

      validateAddCompletionCriterionRequest(
        projectId: string,
        activityId: string,
        addCCRequest: CompletionCriterionRequest,
      ) {
        return validateAddCompletionCriterionRequest(
          this.project(projectId),
          this.activity(projectId, activityId),
          addCCRequest,
          this.projectMembers(projectId),
        );
      },

      validateUpdateCCRequest(
        projectId: string,
        activityId: string,
        criterionId: string,
        CCPayload: CompletionCriterionUpdateRequest,
      ) {
        const activity = this.activityFromAnywhere(projectId, activityId);
        const criterion = this.criterionFromAnywhere(
          projectId,
          activityId,
          criterionId,
        );

        return validateUpdateCCRequest(
          this.project(projectId),
          CCPayload,
          criterion,
          activity,
          this.projectMembers(projectId),
        );
      },

      validateRemoveCCRequest(
        projectId: string,
        activityId: string,
        criterionId: string,
      ) {
        return validateRemoveCCRequest(
          this.activity(projectId, activityId),
          this.criterion(projectId, activityId, criterionId),
        );
      },

      isCCUnplanned(
        projectId: string,
        activityId: string,
        criterionId: string,
      ) {
        const criterion = this.hasActivity(projectId, activityId)
          ? this.criterion(projectId, activityId, criterionId)
          : this.criterionFromProject(projectId, activityId, criterionId);

        return criterion.state === 'unplanned';
      },

      isCCDone(projectId: string, activityId: string, criterionId: string) {
        const criterion = this.hasActivity(projectId, activityId)
          ? this.criterion(projectId, activityId, criterionId)
          : this.criterionFromProject(projectId, activityId, criterionId);

        return criterion.state === 'done';
      },

      isCCStateDisabled(
        fullState: ItemMode | string,
        projectId: string,
        activityId: string,
        criterionId: string,
      ): boolean {
        const [toState] = fullState.split('-');
        const activity = this.activityFromAnywhere(projectId, activityId);
        const criterion = this.criterionFromAnywhere(
          projectId,
          activityId,
          criterionId,
        );

        // Cannot transition to notRequired if activity is done
        if (toState === 'notRequired' && activity.state === 'done') {
          return true;
        }

        // If current state is notRequired, check allowed transitions based on parent activity state
        if (criterion.state === 'notRequired') {
          // From notRequired, transitions depend on parent activity state
          if (activity.state === 'unplanned') {
            // Can only go to unplanned
            return toState !== 'unplanned';
          }
          if (activity.state === 'planned') {
            // Can go to planned, underway, or done
            return toState === 'unplanned';
          }
          if (activity.state === 'underway') {
            // Can go to planned, underway, or done (not unplanned)
            return toState === 'unplanned';
          }
          if (activity.state === 'done') {
            // Can only go to done
            return toState !== 'done';
          }
        }

        // Allow transition to notRequired (already checked activity.state above)
        if (toState === 'notRequired') {
          return false;
        }

        // Regular transitions (not involving notRequired)
        // From unplanned state, check what's allowed based on activity state
        if (criterion.state === 'unplanned') {
          // When activity is unplanned, criterion cannot transition to other states
          // except notRequired (which was already handled above)
          if (activity.state === 'unplanned') {
            return toState !== 'unplanned'; // Can't select unplanned again
          }

          // For other activity states, follow normal progression rules
          return false; // Allow transitions
        }

        // For non-unplanned states, prevent going back to unplanned
        return toState === 'unplanned';
      },

      isCCActionsDisabled(
        projectId: string,
        activityId: string,
        criterionId: string,
      ) {
        return (
          !this.isCCResponsibleOrAbove(projectId, activityId, criterionId) ||
          this.isActivityDone(projectId, activityId)
        );
      },

      isActionDone(projectId: string, activityId: string, actionId: string) {
        const action = this.actionFromAnywhere(projectId, activityId, actionId);

        return action.state === 'done';
      },

      isActionUnplanned(
        projectId: string,
        activityId: string,
        actionId: string,
      ) {
        const action = this.actionFromAnywhere(projectId, activityId, actionId);

        return action.state === 'unplanned';
      },

      isActionStateDisabled(
        fullState: ItemMode | string,
        projectId: string,
        activityId: string,
        actionId: string,
      ): boolean {
        const [toState] = fullState.split('-');

        return (
          this.isActionUnplanned(projectId, activityId, actionId) ||
          toState === 'unplanned'
        );
      },

      validateAddActionRequest(
        projectId: string,
        activityId: string,
        addActionRequest: ActionRequest,
      ) {
        return validateAddActionRequest(
          this.project(projectId),
          this.activity(projectId, activityId),
          addActionRequest,
          this.projectMembers(projectId),
        );
      },

      validateUpdateActionRequest(
        projectId: string,
        activityId: string,
        actionPayload: ActionUpdateRequest,
        actionId: string,
      ) {
        const activity = this.activityFromAnywhere(projectId, activityId);
        const action = this.actionFromAnywhere(projectId, activityId, actionId);

        return validateUpdateActionRequest(
          this.project(projectId),
          actionPayload,
          action,
          activity,
          this.projectMembers(projectId),
        );
      },

      validateRemoveActionRequest(
        projectId: string,
        activityId: string,
        actionId: string,
      ) {
        return validateRemoveActionRequest(
          this.activity(projectId, activityId),
          this.action(projectId, activityId, actionId),
        );
      },

      isActionOperationsDisabled(
        projectId: string,
        activityId: string,
        actionId: string,
      ) {
        return (
          !this.isActionResponsibleOrAbove(projectId, activityId, actionId) ||
          this.isActivityDone(projectId, activityId)
        );
      },

      validateAddApproverRequest(
        projectId: string,
        activityId: string,
        functionId: string,
      ) {
        return validateAddApproverRequest(
          this.project(projectId),
          this.activity(projectId, activityId),
          functionId,
          this.projectMembers(projectId),
        );
      },

      validateRemoveApproverRequest(
        projectId: string,
        activityId: string,
        approverId: string,
      ) {
        return validateRemoveApproverRequest(
          this.activity(projectId, activityId),
          approverId,
        );
      },

      isUserApprover(
        projectId: string,
        activityId: string,
        approverId: string,
      ) {
        return isUserApprover(
          this.user.id,
          this.activity(projectId, activityId),
          approverId,
        );
      },

      validateUpdateActivityApprovalRequest(
        projectId: string,
        activityId: string,
        approverId: string,
      ) {
        return validateUpdateActivityApprovalRequest(
          this.activity(projectId, activityId),
          approverId,
        );
      },

      validateRequestActivityApprovalRequest(
        projectId: string,
        approvalId: string,
        activityId: string,
      ) {
        return validateRequestActivityApprovalRequest(
          approvalId,
          this.activity(projectId, activityId),
        );
      },

      validateAddWatcherRequest(
        projectId: string,
        activityId: string,
        functionId: string,
      ) {
        return validateAddWatcherRequest(
          this.project(projectId),
          this.activity(projectId, activityId),
          functionId,
        );
      },

      validateRemoveWatcherRequest(
        projectId: string,
        activityId: string,
        functionId: string,
      ) {
        return validateRemoveWatcherRequest(
          this.project(projectId),
          this.activity(projectId, activityId),
          functionId,
        );
      },

      validateActivityAddLinkRequest(
        projectId: string,
        activityId: string,
        linkId: string,
      ) {
        return validateActivityAddLinkRequest(
          this.project(projectId),
          this.activity(projectId, activityId),
          linkId,
        );
      },

      validateRemoveLinkFromActivityRequest(
        projectId: string,
        activityId: string,
        linkId: string,
      ) {
        return validateRemoveLinkFromActivityRequest(
          this.project(projectId),
          this.activity(projectId, activityId),
          linkId,
        );
      },

      validateAddTagToActivityRequest(
        projectId: string,
        activityId: string,
        createTagRequest: ActivityTagCreate,
      ) {
        return validateAddTagToActivityRequest(
          this.project(projectId),
          this.activity(projectId, activityId),
          createTagRequest,
        );
      },

      validateRemoveTagFromActivityRequest(
        projectId: string,
        activityId: string,
        tagId: string,
      ) {
        return validateRemoveTagFromActivityRequest(
          this.project(projectId),
          this.activity(projectId, activityId),
          tagId,
        );
      },

      validateMoveTaskRequest(
        projectId: string,
        targetActivityId: string,
        sourceActivityId: string,
        taskState: TaskItemState,
        isAction: boolean,
      ) {
        const sourceActivity = this.activityFromProject(
          projectId,
          sourceActivityId,
        );
        const targetActivity = this.activityFromProject(
          projectId,
          targetActivityId,
        );
        const isResponsibleOrAbove = () => {
          return (
            this.isActivityResponsibleOrAbove(projectId, targetActivityId) &&
            this.isActivityResponsibleOrAbove(projectId, sourceActivityId)
          );
        };
        const validateRequest = () => {
          if (isAction) {
            return validateMoveActionRequest(
              targetActivity.state,
              sourceActivity.state,
              taskState as STATES,
            );
          } else {
            return validateMoveCompletionCriterionRequest(
              targetActivity.state,
              sourceActivity.state,
              taskState,
            );
          }
        };
        const validationResult = validateRequest();

        return !validationResult && isResponsibleOrAbove();
      },
    },
  },
);
